import { Dispatch } from 'redux';
import lmpPersonalizationPopupPermissions from '../common/personalization/components/personalization-modal/personalization-popup-permissions.helper';
import API_URLS from '../constants/api-urls';
import Routes from '../constants/routes';
import IconLoggedUser from '../images/logged-user-icon.svg';
import { updatePersonalizationStatus } from '../api/personalization/update-personalization-status';
import axios from '../utils/axios';
import clearStorage from '../utils/browser-storage/clear-storage';
import { storageKeys } from '../utils/browser-storage/storage-keys';
import emailHash from '../utils/email-hash';
import imagePathCreator from '../utils/image-path-creator/image-path-creator';
import pubsub from '../utils/pubsub';
import SchoolAccount from '../utils/school-account';
import session from '../utils/session';
import makeUrl from '../utils/url/make-url';
import { setUserData } from '../utils/user/user';
import { setCurrency } from './currency';

export const USER_GET = 'user/GET';
export const PERSONALIZATION_GET = 'p/GET';
export const CHANGE_USER_STATE = 'chu';
export const CHANGE_USER_DETAILS = 'changeUserDetails';
export const ACCEPT_AGB = 'acceptAgb';
export const DECLINE_AGB = 'declineAgb';

const initialState: User = {
    details: {} as UserDetails,
    doubleOptIn: true,
    isAgbAccepted: true,
    loaded: false,
    userImagePath: ''
} as User;

type UserActionType = { type: string; payload?: Partial<typeof initialState | UserDetails> };

export default (state = initialState, action: UserActionType) => {
    switch (action.type) {
        case CHANGE_USER_STATE:
        case PERSONALIZATION_GET:
            return {
                ...state,
                ...action.payload
            };
        case USER_GET:
            return {
                ...state,
                ...action.payload,
                loaded: true
            };
        case CHANGE_USER_DETAILS:
            return {
                ...state,
                details: { ...state.details, ...action.payload }
            };
        case ACCEPT_AGB:
            return {
                ...state,
                isAgbAccepted: true
            };
        case DECLINE_AGB:
            return {
                ...state,
                isAgbAccepted: false
            };
        default:
            return state;
    }
};

export const getUser = () => async (dispatch: Dispatch, getState: () => RootState) => {
    const [user, profileDetails] = await Promise.all([
        axios.get(API_URLS.USER_DATA).then(({ data }) => data.items[0] as User),
        axios.get(`${API_URLS.USER_DATA}/details`).then(({ data }) => data.items as User['profileDetails'])
    ]).catch(() => {
        clearStorage();

        return [];
    });

    const { main } = getState();

    if (window.location.pathname === Routes.AUTHOR_REDIRECT.path) {
        if (!user.isSeller) {
            window.location.href = Routes.CABINET_SALES.path;
        }

        window.location.href = makeUrl(Routes.AUTHOR_MAIN.path, {
            id: user.id,
            slug: user.slug
        });
    }

    user.userImagePath = user.details.imagePath
        ? imagePathCreator.croppedImg({ url: user.details.imagePath }, { h: 32, q: 95, w: 32 })
        : IconLoggedUser;

    user.userName = user.details.publicName;

    window.storage.setItem(storageKeys.user.userId, user.id);
    window.storage.setItem(storageKeys.user.creationSource, user.creationSource);
    window.storage.setItem(storageKeys.user.userSlug, user.slug);
    window.storage.setItem(storageKeys.user.isSeller, user.isSeller);
    window.storage.setItem(storageKeys.user.userSD, user.createdAt ? user.createdAt : '');
    emailHash.storeMD5(user.email);
    emailHash.storeSHA256(user.email);

    user.profileDetails = profileDetails;
    user.profileDetails.invoiceDetails.clientEmail = profileDetails.invoiceDetails.email;
    user.loggedInAsAnotherUser = window.storage.getItem(storageKeys.user.impersonated) === '1';
    user.availableCredits = profileDetails.details.credits ? parseFloat(profileDetails.details.credits.amount) : 0;
    user.hadCredits = profileDetails.details.credits ? profileDetails.details.credits.had_credits : false;

    setUserData(user);
    dispatch({ type: USER_GET, payload: user });
    setCurrency({ code: profileDetails?.details?.currencyCode })(dispatch);

    //must run after authorization
    const personalization = await axios({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: `Bearer ${session.getAccessToken()}` },
        url: 'v3/personalisations/get'
    }).then((data) => data as unknown as PersonalisationResponseType);

    const preferences = personalization.result || [];
    const permissions = lmpPersonalizationPopupPermissions();
    const isAdmin = SchoolAccount.isAdmin(main?.user);
    const showPopup = personalization.showPopup && permissions.showPopup;

    if (showPopup && !main?.user?.personalisationRequested && !isAdmin) {
        setTimeout(() => {
            pubsub.publish('showPersonalization', { preferences, type: 'auto' });
        }, 10000);
    }

    dispatch({
        payload: {
            needActualisation: personalization.needActualisation,
            personalisationRequested: true,
            preferences,
            showPopup: personalization.showPopup
        },
        type: PERSONALIZATION_GET
    });
};

export const updateP13nStatus = () => (dispatch: Dispatch) => {
    updatePersonalizationStatus().then(() => {
        dispatch({
            payload: {
                needActualisation: false,
                showPopup: false
            },
            type: PERSONALIZATION_GET
        });
    });
};

export const setLoaded = () => (dispatch: Dispatch) => {
    dispatch({
        payload: {
            loaded: true
        },
        type: CHANGE_USER_STATE
    });
};

export const changeUserDetails = (details: Partial<UserDetails>) => (dispatch: Dispatch) => {
    dispatch({
        payload: details,
        type: CHANGE_USER_DETAILS
    });
};

export const acceptAgb = () => (dispatch: Dispatch) => {
    dispatch({
        type: ACCEPT_AGB
    });
};

export const declineAgb = () => (dispatch: Dispatch) => {
    dispatch({
        type: DECLINE_AGB
    });
};

type PersonalisationResponseType = {
    result: Partial<User>;
    showPopup: boolean;
    needActualisation: boolean;
};
